import { Check, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/cn';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface AutoSaveIndicatorProps {
  status: SaveStatus;
  onRetry?: () => void;
  /** Clear, user-facing message shown in place of the generic "Error saving". */
  message?: string;
  className?: string;
}

export function AutoSaveIndicator({ status, onRetry, message, className }: AutoSaveIndicatorProps) {
  if (status === 'idle') return null;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300',
        status === 'saving' && 'bg-gray-100 text-gray-600',
        status === 'saved' && 'bg-emerald-50 text-emerald-700',
        status === 'error' && 'bg-red-50 text-red-700',
        className
      )}
    >
      {status === 'saving' && (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Saving...</span>
        </>
      )}

      {status === 'saved' && (
        <>
          <div className="relative">
            <Check className="w-4 h-4" />
            {/* Pulse animation */}
            <div className="absolute inset-0 animate-ping">
              <Check className="w-4 h-4 opacity-40" />
            </div>
          </div>
          <span>Saved</span>
        </>
      )}

      {status === 'error' && (
        <>
          <AlertCircle className="w-4 h-4" />
          <span>{message || 'Error saving'}</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="ml-1 p-1 rounded hover:bg-red-100 transition-colors"
              title="Retry"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}
